import type { IActionState } from '../../interface/state/IActionState';

export interface ICategorizedAction {
	id: number;
	rawName: string;
	displayName: string;
}

export interface IActionSubcategory {
	key: string;
	label: string;
	actions: ICategorizedAction[];
}

export interface IActionCategory {
	key: string;
	label: string;
	subcategories: IActionSubcategory[];
}

export const DOMAIN_LABELS: Record<string, string> = {
	system_management: 'System Management',
	election: 'Elections',
	electoral_division: 'Electoral Divisions',
	result: 'Results',
	volunteer: 'Volunteers & Agents',
	report: 'Reports',
	audit: 'Audit & Logs',
	notification: 'Notifications',
	dashboard: 'Dashboard',
	polling_unit: 'Polling Units',
	ward: 'Wards',
	lga: 'Local Government Areas',
	state: 'States',
	zone: 'Zones',
	constituency: 'Constituencies',
	party: 'Parties',
	candidate: 'Candidates',
	user: 'Users',
	role: 'Roles',
	action: 'Actions',
	permission: 'Permissions',
	setting: 'Settings',
	upload: 'Uploads',
	extraction: 'Extraction',
	organization: 'Organization',
};

const OPERATION_LABELS: Record<string, string> = {
	create: 'Create',
	get: 'View',
	get_all: 'View All',
	update: 'Update',
	delete: 'Delete',
	approve: 'Approve',
	reject: 'Reject',
	assign: 'Assign',
	unassign: 'Unassign',
	export: 'Export',
	import: 'Import',
	upload: 'Upload',
	download: 'Download',
	manage: 'Manage',
	view: 'View',
	edit: 'Edit',
	list: 'List',
	search: 'Search',
	filter: 'Filter',
	configure: 'Configure',
	activate: 'Activate',
	deactivate: 'Deactivate',
	publish: 'Publish',
	archive: 'Archive',
};

/**
 * Parse an action name like "system_management_roles_create" into parts.
 * Uses longest prefix match against known domain labels.
 *
 * F5.02 — When a `hint.domain` is supplied (e.g. from
 * `IActionState.domain` once the backend ships it), the longest-prefix
 * heuristic is bypassed and the hint is trusted as canonical. The remainder
 * is computed by stripping the hint from the raw name. This lets new domains
 * surface correctly the moment the backend starts emitting them, without
 * waiting for a frontend code change to extend `DOMAIN_LABELS`.
 */
export const parseActionName = (raw: string, hint?: { domain?: string }): { domain: string; remainder: string } => {
	const lower = raw.toLowerCase();

	if (hint?.domain) {
		const domain = hint.domain.toLowerCase();
		const remainder = lower.startsWith(`${domain}_`) ? lower.slice(domain.length + 1) : lower === domain ? '' : lower; // hint disagrees with raw name; surface the full name as remainder
		return { domain, remainder };
	}

	// Sort domain keys by length (longest first) for greedy matching
	const sortedDomains = Object.keys(DOMAIN_LABELS).sort((a, b) => b.length - a.length);

	for (const domain of sortedDomains) {
		if (lower.startsWith(domain + '_') || lower === domain) {
			const remainder = lower.startsWith(domain + '_') ? lower.slice(domain.length + 1) : '';
			return { domain, remainder };
		}
	}

	// Fallback: use first segment as domain
	const parts = lower.split('_');
	const domain = parts[0];
	const remainder = parts.slice(1).join('_');
	return { domain, remainder };
};

/**
 * Convert a raw action name to a human-readable display name.
 */
export const humanizeActionName = (raw: string): string => {
	const { remainder } = parseActionName(raw);

	if (!remainder) return titleCase(raw.replace(/_/g, ' '));

	// Try to match known operations
	const sortedOps = Object.keys(OPERATION_LABELS).sort((a, b) => b.length - a.length);
	for (const op of sortedOps) {
		if (remainder.startsWith(op + '_') || remainder === op) {
			const operationLabel = OPERATION_LABELS[op];
			const subject = remainder.startsWith(op + '_') ? remainder.slice(op.length + 1) : '';
			if (subject) {
				return `${operationLabel} ${titleCase(subject.replace(/_/g, ' '))}`;
			}
			return operationLabel;
		}
	}

	return titleCase(remainder.replace(/_/g, ' '));
};

const titleCase = (str: string): string =>
	str
		.split(' ')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');

/**
 * Group an array of actions into categories by domain.
 */
export const categorizeActions = (actions: IActionState[]): IActionCategory[] => {
	const categoryMap = new Map<string, Map<string, ICategorizedAction[]>>();

	for (const action of actions) {
		// F5.02 — honor a backend-provided `domain` hint when present, otherwise
		// fall back to the longest-prefix heuristic.
		const { domain, remainder } = parseActionName(action.name, { domain: action.domain });
		const displayName = humanizeActionName(action.name);

		// F5.02 — backend-provided `category` wins over the heuristic.
		// Determine subcategory from remainder when not provided.
		let subcategoryKey = action.category?.toLowerCase() || 'general';
		if (!action.category && remainder) {
			const parts = remainder.split('_');
			// If there are more than 1 parts and the first part is an operation,
			// use the subject as the subcategory
			const sortedOps = Object.keys(OPERATION_LABELS).sort((a, b) => b.length - a.length);
			let matched = false;
			for (const op of sortedOps) {
				if (remainder.startsWith(op + '_')) {
					const subject = remainder.slice(op.length + 1);
					if (subject) {
						subcategoryKey = subject.split('_')[0];
					}
					matched = true;
					break;
				}
			}
			if (!matched && parts.length > 1) {
				subcategoryKey = parts[0];
			}
		}

		if (!categoryMap.has(domain)) {
			categoryMap.set(domain, new Map());
		}
		const subMap = categoryMap.get(domain)!;
		if (!subMap.has(subcategoryKey)) {
			subMap.set(subcategoryKey, []);
		}

		subMap.get(subcategoryKey)!.push({
			id: action.id,
			rawName: action.name,
			displayName,
		});
	}

	const categories: IActionCategory[] = [];

	for (const [domainKey, subMap] of categoryMap) {
		const label = DOMAIN_LABELS[domainKey] || titleCase(domainKey.replace(/_/g, ' '));
		const subcategories: IActionSubcategory[] = [];

		for (const [subKey, actions] of subMap) {
			subcategories.push({
				key: subKey,
				label: subKey === 'general' ? 'General' : titleCase(subKey.replace(/_/g, ' ')),
				actions: actions.sort((a, b) => a.displayName.localeCompare(b.displayName)),
			});
		}

		subcategories.sort((a, b) => a.label.localeCompare(b.label));

		categories.push({
			key: domainKey,
			label,
			subcategories,
		});
	}

	return categories.sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Get a summary of assigned vs total actions per category.
 */
export const getCategorySummary = (
	categories: IActionCategory[],
	assignedSet: Set<string>
): { label: string; total: number; assigned: number }[] =>
	categories.map((cat) => {
		const allActions = cat.subcategories.flatMap((sub) => sub.actions);
		const total = allActions.length;
		const assigned = allActions.filter((a) => assignedSet.has(a.rawName)).length;
		return { label: cat.label, total, assigned };
	});
